import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SubjectPill } from "@/lib/accents";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Play, Sparkles, CheckCircle2, ChevronRight, Loader2, BookOpen, Layers, AlertTriangle, Flame } from "lucide-react";

function StudyStreak({ streak, activity }) {
  const days = activity || [];
  const max = Math.max(1, ...days.map((d) => d.count));
  const totalDays = days.length;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="study-streak">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${streak > 0 ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-400"}`}>
            <Flame className="h-6 w-6" />
          </span>
          <div>
            <div className="font-display text-2xl font-bold text-slate-900 leading-none" data-testid="streak-count">
              {streak} day{streak === 1 ? "" : "s"}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {streak > 0 ? "Current study streak — keep it going!" : "Study today to start a streak"}
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-400">Last 14 days</div>
      </div>
      <div className="mt-5 flex items-end gap-1.5" data-testid="activity-chart">
        {days.map((d, i) => {
          const isToday = i === days.length - 1;
          const h = d.count > 0 ? Math.max(6, Math.round((d.count / max) * 64)) : 2;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1"
              title={`${d.date}: ${d.count} review${d.count === 1 ? "" : "s"}`}>
              <div className="w-full flex items-end justify-center" style={{ height: 64 }}>
                <div className="w-full rounded-t-md transition-all"
                  style={{ height: `${h}px`, backgroundColor: d.count > 0 ? (isToday ? "#f97316" : "#FFC200") : "#e2e8f0" }} />
              </div>
              <span className={`text-[10px] ${isToday ? "text-slate-900 font-semibold" : "text-slate-400"}`}>{d.label?.[0]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MasteryRing({ pct }) {
  const r = 9;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, pct)) / 100) * c;
  const tone = pct >= 80 ? "#059669" : pct >= 40 ? "#FFC200" : "#94a3b8";
  return (
    <span className="inline-flex items-center gap-1.5" title={`${pct}% mastered`} data-testid="mastery-ring">
      <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0 -rotate-90">
        <circle cx="12" cy="12" r={r} fill="none" stroke="#e2e8f0" strokeWidth="3" />
        <circle cx="12" cy="12" r={r} fill="none" stroke={tone} strokeWidth="3"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <span className="text-xs font-medium text-slate-500 tabular-nums">{pct}%</span>
    </span>
  );
}


export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/student/dashboard")).data,
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const due = data?.due_today ?? 0;
  const newCards = data?.new_available ?? 0;
  const learn = data?.learn_available ?? 0;
  const courses = data?.courses ?? [];
  const nothing = due === 0 && newCards === 0 && learn === 0;
  const startOnly = due === 0 && newCards === 0 && learn > 0;

  const startTopic = (courseId, topicId) =>
    navigate(`/flashcards/study?course=${courseId}${topicId ? `&topic=${topicId}` : ""}`);
  const startDeck = (courseId, deckId) =>
    navigate(`/flashcards/study?course=${courseId}&deck=${deckId}`);
  const startWeak = () => navigate("/flashcards/study?mode=weak");
  const weak = data?.weak_available ?? 0;

  return (
    <div className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Dashboard</p>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold text-slate-900" data-testid="dashboard-welcome">
          Welcome back, {data?.name?.split(" ")[0]}
        </h1>
      </div>

      <StudyStreak streak={data?.streak ?? 0} activity={data?.activity ?? []} />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        {nothing ? (
          <div className="text-center py-6" data-testid="dashboard-empty">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <h2 className="mt-3 font-display text-2xl font-semibold text-slate-900">You're up to date</h2>
            <p className="mt-1 text-slate-500">No cards are due right now. Your next reviews will appear soon.</p>
            {data?.allow_topic_practice && courses.length > 0 && (
              <Button onClick={() => navigate("/flashcards/courses")} data-testid="dashboard-extra-practice"
                className="mt-5 bg-[#FFC200] text-[#141821] hover:bg-[#141821] hover:text-[#FFC200] rounded-full font-semibold transition-colors">
                <Sparkles className="h-4 w-4 mr-2" /> Get ahead with Extra Practice
              </Button>
            )}
            {weak > 0 && (
              <div className="mt-4">
                <Button onClick={startWeak} data-testid="weak-cards-button" variant="outline"
                  className="rounded-full font-semibold border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 transition-colors">
                  <AlertTriangle className="h-4 w-4 mr-2" /> Practice {weak} weak card{weak === 1 ? "" : "s"}
                </Button>
                <p className="mt-2 text-xs text-slate-400">Cards you've struggled with — this won't change your schedule.</p>
              </div>
            )}
          </div>
        ) : startOnly ? (
          <div className="py-2" data-testid="dashboard-start-learning">
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-teal-600 mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl font-semibold text-slate-900">Ready to start learning</h2>
                <p className="mt-1 text-slate-500">You have no reviews due yet. Choose a topic below to learn your first cards — your progress and schedule build from there.</p>
              </div>
            </div>
            {weak > 0 && (
              <Button onClick={startWeak} data-testid="weak-cards-button" variant="outline"
                className="mt-5 rounded-full font-semibold border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 transition-colors">
                <AlertTriangle className="h-4 w-4 mr-2" /> Practice {weak} weak card{weak === 1 ? "" : "s"}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <div className="font-display text-5xl font-bold text-slate-900" data-testid="dashboard-due-count">{due}</div>
                <div className="text-slate-500 mt-1">cards due today</div>
              </div>
              {data?.include_new_cards && (
              <div className="flex items-center gap-2 text-slate-500">
                <Sparkles className="h-4 w-4 text-teal-600" />
                <span data-testid="dashboard-new-count">{newCards} new cards available</span>
              </div>
            )}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/flashcards/study")} data-testid="start-revision-button"
                className="w-full sm:w-auto bg-[#FFC200] text-[#141821] hover:bg-[#141821] hover:text-[#FFC200] h-12 px-8 text-base rounded-full font-semibold transition-colors">
                <Play className="h-5 w-5 mr-2" /> Revise all due today
              </Button>
              {weak > 0 && (
                <Button onClick={startWeak} data-testid="weak-cards-button" variant="outline"
                  className="w-full sm:w-auto h-12 px-8 text-base rounded-full font-semibold border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 transition-colors">
                  <AlertTriangle className="h-5 w-5 mr-2" /> Weak spots ({weak})
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Revise by topic</h2>
            <p className="text-sm text-slate-500 mt-0.5">Pick a course, then a topic. Reviews still update your schedule.</p>
          </div>
          {data?.allow_topic_practice && (
            <button onClick={() => navigate("/flashcards/courses")} data-testid="study-by-topic-link"
              className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 shrink-0">
              Extra practice <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {courses.length === 0 ? (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="no-courses">
            <p className="text-slate-600">No flashcard courses have been assigned to your account yet.</p>
            <p className="text-sm text-slate-400 mt-1">Please contact Science Sorted if you believe this is incorrect.</p>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {courses.map((c) => (
              <div key={c.id} data-testid={`course-card-${c.id}`}
                className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="p-5 flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <SubjectPill subject={c.subject}>{c.exam_board || c.subject}</SubjectPill>
                    <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">{c.name}</h3>
                    <div className="mt-1 flex items-center gap-4 text-sm">
                      <span className="text-slate-900 font-medium">{c.due_count} due</span>
                      {c.new_count > 0 && <span className="text-teal-600">{c.new_count} new</span>}
                      <MasteryRing pct={c.mastered_pct ?? 0} />
                    </div>
                  </div>
                  <Button onClick={() => startTopic(c.id)} data-testid={`revise-course-${c.id}`}
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-full font-semibold transition-colors">
                    <Layers className="h-4 w-4 mr-2" /> Revise whole course
                  </Button>
                </div>

                {c.topics && c.topics.length > 0 && (
                  <Accordion type="single" collapsible className="border-t border-slate-100">
                    <AccordionItem value="topics" className="border-0">
                      <AccordionTrigger className="px-5 text-sm font-medium text-slate-700 hover:no-underline"
                        data-testid={`toggle-topics-${c.id}`}>
                        Choose a topic
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3">
                        <div className="space-y-1">
                          {c.topics.map((t) => {
                            const total = t.due_count + t.new_count;
                            const decks = t.decks || [];
                            return (
                              <div key={t.id}>
                                <button data-testid={`revise-topic-${t.id}`}
                                  onClick={() => startTopic(c.id, t.id)}
                                  className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-left hover:bg-slate-50 transition-colors group">
                                  <span className="flex items-center gap-2 text-slate-700 font-medium">
                                    <BookOpen className="h-4 w-4 text-slate-400" /> {t.name}
                                  </span>
                                  <span className="flex items-center gap-3 text-xs">
                                    <MasteryRing pct={t.mastered_pct ?? 0} />
                                    {total === 0 ? (
                                      <span className="text-slate-400">Up to date</span>
                                    ) : (
                                      <>
                                        <span className="text-slate-900 font-medium">{t.due_count} due</span>
                                        {t.new_count > 0 && <span className="text-teal-600">{t.new_count} new</span>}
                                      </>
                                    )}
                                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                                  </span>
                                </button>
                                {decks.length > 1 && (
                                  <div className="pl-9 pr-2 pb-1 space-y-0.5">
                                    {decks.map((d) => {
                                      const dtotal = d.due_count + d.new_count;
                                      return (
                                        <button key={d.id} data-testid={`revise-deck-${d.id}`}
                                          onClick={() => startDeck(c.id, d.id)}
                                          className="w-full flex items-center justify-between rounded-md px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-100 transition-colors">
                                          <span className="flex items-center gap-2">
                                            <Layers className="h-3.5 w-3.5 text-slate-300" /> {d.name}
                                          </span>
                                          <span className="flex items-center gap-2 text-xs">
                                            <MasteryRing pct={d.mastered_pct ?? 0} />
                                            {dtotal === 0 ? (
                                              <span className="text-slate-400">Up to date</span>
                                            ) : (
                                              <>
                                                <span className="text-slate-700 font-medium">{d.due_count} due</span>
                                                {d.new_count > 0 && <span className="text-teal-600">{d.new_count} new</span>}
                                              </>
                                            )}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
